import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { api } from '../../api';
import { toast } from 'sonner';
import {
  Users, Trash2, Edit2, X, ToggleLeft, ToggleRight, Info, ArrowRight,
} from 'lucide-react';

const ROLE_COLORS = {
  partner_admin:    { bg: 'bg-purple-50 border-purple-200', badge: 'bg-purple-100 text-purple-800', dot: 'bg-purple-500' },
  content_admin:    { bg: 'bg-blue-50 border-blue-200',     badge: 'bg-blue-100 text-blue-800',     dot: 'bg-blue-500'   },
  ms_stakeholder:   { bg: 'bg-amber-50 border-amber-200',   badge: 'bg-amber-100 text-amber-800',   dot: 'bg-amber-500'  },
  training_partner: { bg: 'bg-gray-50 border-gray-200',     badge: 'bg-gray-100 text-gray-700',     dot: 'bg-gray-400'   },
};

const EmptyRoleMappingForm = {
  name: '', description: '', match_field: 'user_role', operator: 'equals', match_value: '', assigned_role: 'partner_admin', priority: 0, is_active: true,
};

const SYSTEM_DEFAULTS = [
  { match_field: 'user_role', operator: 'equals',  match_value: 'Partner Org RM',               assigned_role: 'partner_admin',    label: 'Partner Org RM → Partner Admin' },
  { match_field: 'domain',    operator: 'in',      match_value: 'technofocus.co, technofocus.in', assigned_role: 'content_admin',   label: 'Technofocus domain → Content Admin' },
  { match_field: '*',         operator: '(default)', match_value: '',                             assigned_role: 'training_partner', label: 'All other users → Training Partner' },
];

/**
 * Role Mapping tab: custom DB-driven rules + read-only system-default fallbacks + form modal.
 * `openCreateMapping` is exposed via ref so the shared header "Add Mapping" button can trigger it.
 */
const RoleMappingTab = forwardRef(function RoleMappingTab({ mappingRules, setMappingRules, config, reload }, ref) {
  const [showMappingForm, setShowMappingForm] = useState(false);
  const [editingMapping, setEditingMapping]   = useState(null);
  const [mappingForm, setMappingForm]         = useState(EmptyRoleMappingForm);
  const [saving, setSaving]                   = useState(false);

  const getRoleLabel = (val) => config.portal_roles?.find(r => r.value === val)?.label || val;

  const openCreateMapping = () => {
    setEditingMapping(null);
    setMappingForm({ ...EmptyRoleMappingForm, match_field: config.token_fields[0]?.field || 'user_role' });
    setShowMappingForm(true);
  };
  const openEditMapping = (rule) => { setEditingMapping(rule); setMappingForm({ ...rule }); setShowMappingForm(true); };

  useImperativeHandle(ref, () => ({ openCreateMapping }));

  const saveMappingRule = async () => {
    if (!mappingForm.name.trim()) { toast.error('Rule name is required'); return; }
    if (!mappingForm.match_value.trim()) { toast.error('Match value is required'); return; }
    setSaving(true);
    try {
      if (editingMapping) {
        await api.request(`/admin/role-mapping-rules/${editingMapping.id}`, { method: 'PUT', body: JSON.stringify(mappingForm) });
        toast.success('Role mapping updated');
      } else {
        await api.request('/admin/role-mapping-rules', { method: 'POST', body: JSON.stringify(mappingForm) });
        toast.success('Role mapping created');
      }
      setShowMappingForm(false); reload();
    } catch { toast.error('Failed to save role mapping'); }
    finally { setSaving(false); }
  };

  const deleteMappingRule = async (id) => {
    if (!window.confirm('Delete this role mapping rule?')) return;
    try {
      await api.request(`/admin/role-mapping-rules/${id}`, { method: 'DELETE' });
      toast.success('Role mapping deleted');
      setMappingRules(r => r.filter(x => x.id !== id));
    } catch { toast.error('Failed to delete rule'); }
  };

  const toggleMappingRule = async (rule) => {
    try {
      await api.request(`/admin/role-mapping-rules/${rule.id}`, { method: 'PUT', body: JSON.stringify({ is_active: !rule.is_active }) });
      setMappingRules(r => r.map(x => x.id === rule.id ? { ...x, is_active: !x.is_active } : x));
    } catch { toast.error('Failed to toggle rule'); }
  };

  return (
    <>
      {/* Explanation */}
      <div className="p-4 bg-purple-50 border border-purple-100 rounded-xl">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-purple-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-purple-800 mb-1">How Role Mapping Works</p>
            <p className="text-xs text-purple-700">
              When a LevelUp user logs in via SSO, these rules determine which portal role they receive.
              Rules are evaluated <strong>top to bottom by priority</strong>. The first match wins.
              If no rule matches, system defaults apply.
            </p>
          </div>
        </div>
      </div>

      {/* DB-driven rules */}
      {mappingRules.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
          <Users size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No custom role mapping rules</p>
          <p className="text-gray-400 text-sm mt-1">System defaults are active. Add rules to override them.</p>
          <button onClick={openCreateMapping} className="mt-4 px-4 py-2 bg-levelup-accent text-levelup-darker rounded-lg text-sm font-bold">
            Add First Mapping
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {mappingRules.map(rule => {
            const colors = ROLE_COLORS[rule.assigned_role] || ROLE_COLORS.training_partner;
            return (
              <div
                key={rule.id}
                data-testid={`mapping-rule-${rule.id}`}
                className={`border rounded-xl p-4 transition-all ${colors.bg} ${!rule.is_active ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${colors.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800">{rule.name}</span>
                      {!rule.is_active && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Disabled</span>}
                    </div>
                    {rule.description && <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="text-xs bg-white border border-gray-200 rounded px-2 py-0.5 font-mono text-blue-700">
                        {rule.match_field}
                      </span>
                      <span className="text-xs text-gray-400">{rule.operator.replace(/_/g,' ')}</span>
                      <span className="text-xs font-medium text-gray-700">"{rule.match_value}"</span>
                      <ArrowRight size={12} className="text-gray-400" />
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colors.badge}`}>
                        {getRoleLabel(rule.assigned_role)}
                      </span>
                      <span className="text-xs text-gray-400 ml-1">priority {rule.priority}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => toggleMappingRule(rule)}
                      data-testid={`toggle-mapping-${rule.id}`}
                      className="p-2 rounded-lg hover:bg-white/60 text-gray-500 transition-colors"
                      title={rule.is_active ? 'Disable' : 'Enable'}>
                      {rule.is_active ? <ToggleRight size={18} className="text-emerald-600"/> : <ToggleLeft size={18}/>}
                    </button>
                    <button onClick={() => openEditMapping(rule)}
                      data-testid={`edit-mapping-${rule.id}`}
                      className="p-2 rounded-lg hover:bg-white/60 text-gray-600 transition-colors"><Edit2 size={15}/></button>
                    <button onClick={() => deleteMappingRule(rule.id)}
                      data-testid={`delete-mapping-${rule.id}`}
                      className="p-2 rounded-lg hover:bg-red-100 text-red-500 transition-colors"><Trash2 size={15}/></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* System Defaults (always visible, read-only) */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 mt-4">
          System Defaults (fallback when no rule matches)
        </p>
        <div className="space-y-2">
          {SYSTEM_DEFAULTS.map((d, i) => {
            const colors = ROLE_COLORS[d.assigned_role] || ROLE_COLORS.training_partner;
            return (
              <div key={i} className={`border rounded-lg p-3 opacity-70 ${colors.bg}`}>
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <span className="font-mono font-medium text-blue-700">{d.match_field}</span>
                  {d.operator !== '(default)' && (
                    <>
                      <span className="text-gray-400">{d.operator}</span>
                      <span className="font-medium text-gray-700">"{d.match_value}"</span>
                      <ArrowRight size={11} className="text-gray-400" />
                    </>
                  )}
                  {d.operator === '(default)' && <span className="text-gray-400">→</span>}
                  <span className={`font-bold px-2 py-0.5 rounded-full ${colors.badge}`}>
                    {getRoleLabel(d.assigned_role)}
                  </span>
                  <span className="text-gray-400 italic ml-1">{d.label}</span>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          System defaults are always applied as a fallback and cannot be deleted. Create custom rules above to override them.
        </p>
      </div>

      {/* Role Mapping Form Modal */}
      {showMappingForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div data-testid="mapping-form-modal" className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="font-bold text-lg text-gray-900">
                {editingMapping ? 'Edit Role Mapping' : 'Create Role Mapping'}
              </h2>
              <button onClick={() => setShowMappingForm(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Rule Name *</label>
                <input data-testid="mapping-name-input" value={mappingForm.name}
                  onChange={e => setMappingForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Partner Org RM → Partner Admin"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-levelup-accent" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Description</label>
                <input value={mappingForm.description} onChange={e => setMappingForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional explanation"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-levelup-accent" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">When this SSO field…</label>
                <div className="flex gap-2">
                  <select data-testid="mapping-field-select" value={mappingForm.match_field}
                    onChange={e => setMappingForm(f => ({ ...f, match_field: e.target.value }))}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-levelup-accent">
                    {config.token_fields.map(f => (
                      <option key={f.field} value={f.field}>{f.label} ({f.field})</option>
                    ))}
                  </select>
                  <select data-testid="mapping-operator-select" value={mappingForm.operator}
                    onChange={e => setMappingForm(f => ({ ...f, operator: e.target.value }))}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-levelup-accent">
                    {config.operators.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                </div>
                <input data-testid="mapping-value-input" value={mappingForm.match_value}
                  onChange={e => setMappingForm(f => ({ ...f, match_value: e.target.value }))}
                  placeholder={config.token_fields.find(f => f.field === mappingForm.match_field)?.example || 'value'}
                  className="w-full mt-2 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-levelup-accent" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">…assign this portal role</label>
                <select data-testid="mapping-role-select" value={mappingForm.assigned_role}
                  onChange={e => setMappingForm(f => ({ ...f, assigned_role: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-levelup-accent">
                  {config.portal_roles?.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Priority (higher = first)</label>
                  <input type="number" value={mappingForm.priority}
                    onChange={e => setMappingForm(f => ({ ...f, priority: parseInt(e.target.value, 10) || 0 }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-levelup-accent" />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={mappingForm.is_active}
                      onChange={e => setMappingForm(f => ({ ...f, is_active: e.target.checked }))}
                      className="w-4 h-4 accent-levelup-accent" />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t bg-gray-50 rounded-b-2xl">
              <button onClick={() => setShowMappingForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors">Cancel</button>
              <button data-testid="save-mapping-btn" onClick={saveMappingRule} disabled={saving}
                className="px-5 py-2 bg-levelup-accent text-levelup-darker rounded-lg text-sm font-bold disabled:opacity-60 hover:opacity-90 transition-opacity">
                {saving ? 'Saving...' : (editingMapping ? 'Update Mapping' : 'Create Mapping')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default RoleMappingTab;
